<script lang="ts" setup>
import { IMAGE_HOTMATCH_DEFAULT } from '~/constants'
import { DATE_TIME_FORMAT } from '@kira-dancer/nuxt-common/constants'
import { HOT_MATCH_ITEM_DEFAULT } from '~/resources/sports'
import { formatDate } from '@kira-dancer/nuxt-common/utils/date'
import type { MatchData } from '@kira-dancer/nuxt-common'
import { usePLayGameSports } from '~/composables/sports/use-play-game-sports'
import { SportType } from '~/enums/sports'

defineProps({
  hotmatch: {
    type: Object as () => MatchData,
    default: () => HOT_MATCH_ITEM_DEFAULT,
  },
  customClass: {
    type: String,
    default: '',
  },
})
const {
  getLinkKSportsGame,
  getLinkPSportsGame,
  isCheckEmptyPSport,
  isCheckEmptyKSport,
  playSportGame,
} = usePLayGameSports()
</script>
<template>
  <div
    v-if="hotmatch"
    class="hotmatch-item"
    :class="[customClass, { 'hotmatch-item--disabled': hotmatch?.isDisable }]"
  >
    <div class="hotmatch-item__wrapper-header">
      <div class="hotmatch-item__league line-clamp-1">
        {{ hotmatch?.league_name }}
      </div>
      <div class="hotmatch-item__wrapper-date">
        <ClientOnly>
          <div class="hotmatch-item__footer-date">
            {{
              hotmatch.text_time
                ? formatDate(hotmatch.text_time, DATE_TIME_FORMAT.DATE_FORMAT)
                : '--/--/----'
            }}
          </div>
          <div v-if="!hotmatch.live" class="hotmatch-item__footer-time">
            {{
              hotmatch.text_time
                ? formatDate(
                    hotmatch.text_time,
                    DATE_TIME_FORMAT.TIME_DATE_FORMAT_SCHEDULE,
                  )
                : '--:--'
            }}
          </div>
        </ClientOnly>
      </div>
    </div>
    <div class="hotmatch-item__wrapper-content">
      <div class="hotmatch-item__info">
        <div class="hotmatch-item__info-team">
          <div class="hotmatch-item__info-team-wrapper left">
            <div class="hotmatch-item__info-name line-limit-1">
              {{ hotmatch.teams[0]?.name }}
            </div>
            <div class="hotmatch-item__info-team__flat">
              <BaseImage
                :src="hotmatch.teams[0].flag_thumbnail"
                :image-default="IMAGE_HOTMATCH_DEFAULT"
                :alt="hotmatch.teams[0]?.name"
                loading="lazy"
                format="webp"
                width="20"
                height="20"
              />
            </div>
          </div>
          <div class="hotmatch-item__info-name-center">Vs</div>
          <div class="hotmatch-item__info-team-wrapper right">
            <div class="hotmatch-item__info-team__flat">
              <BaseImage
                :src="hotmatch.teams[1]?.flag_thumbnail"
                :alt="hotmatch.teams[1]?.name"
                :image-default="IMAGE_HOTMATCH_DEFAULT"
                loading="lazy"
                format="webp"
                width="20"
                height="20"
              />
            </div>
            <div class="hotmatch-item__info-name line-limit-1">
              {{ hotmatch.teams[1]?.name }}
            </div>
          </div>
        </div>
      </div>
      <div class="hotmatch-item__handicap-wrapper">
        <div class="hotmatch-item__handicap">
          <div class="hotmatch-item__handicap-list">
            <BaseHandicap :handicap="hotmatch.ahFT?.hTeam" />
            <BaseHandicap :handicap="hotmatch.overFT?.hTeam" />
          </div>
          <div class="hotmatch-item__handicap-list">
            <BaseHandicap
              :handicap="hotmatch.ahFT?.aTeam"
              :is-hidden-rate="true"
            />
            <BaseHandicap :handicap="hotmatch.overFT?.aTeam" rate="u" />
          </div>
        </div>
        <div class="hotmatch-item__handicap-text">
          <span class="hotmatch-item__handicap-text-item">Chấp</span>
          <span class="hotmatch-item__handicap-text-item">Tài xỉu</span>
        </div>
        <div class="hotmatch-item__handicap">
          <div class="hotmatch-item__handicap-list">
            <BaseHandicap :handicap="hotmatch.athena_ahFT?.hTeam" />
            <BaseHandicap :handicap="hotmatch.athena_overFT?.hTeam" />
          </div>
          <div class="hotmatch-item__handicap-list">
            <BaseHandicap
              :handicap="hotmatch.athena_ahFT?.aTeam"
              :is-hidden-rate="true"
            />
            <BaseHandicap :handicap="hotmatch.athena_overFT?.aTeam" rate="u" />
          </div>
        </div>
      </div>
    </div>
    <div class="hotmatch-item__footer">
      <div class="hotmatch-item__footer-action">
        <a
          :href="getLinkKSportsGame(hotmatch)"
          :button-id="
            hotmatch.matchId
              ? `bet-K-Sports-match-id-${hotmatch.matchId}`
              : 'bet-K-Sports'
          "
          :class="{
            'v-btn--disabled disabled':
              isCheckEmptyKSport(hotmatch) || hotmatch?.isDisable,
          }"
          button-name="k-sports-hotmatch-betting"
          class="v-btn v-theme--light v-btn--density-default v-btn--size-small v-btn--variant-text base-button k-sports-hotmatch v-btn--primary"
          @click.prevent="playSportGame(SportType.KSports, hotmatch)"
        >
          <img
            class="logo-sports"
            :src="`/images/hotmatch/${
              isCheckEmptyKSport(hotmatch) || hotmatch?.isDisable
                ? 'provider-k-sport-disable.webp'
                : 'provider-k-sport.webp'
            }`"
            alt="Logo K-Sports"
          />
          <span class="v-btn__content">Cược K-Sports</span>
        </a>
        <a
          :href="getLinkPSportsGame(hotmatch)"
          :button-id="
            hotmatch.matchId
              ? `bet-K-Sports-match-id-${hotmatch.matchId}`
              : 'bet-K-Sports'
          "
          :class="{
            'v-btn--disabled disabled':
              isCheckEmptyPSport(hotmatch) || hotmatch?.isDisable,
          }"
          button-name="p-sports-hotmatch-betting"
          class="v-btn v-theme--light v-btn--density-default v-btn--size-small v-btn--variant-text base-button p-sports-hotmatch v-btn--primary"
          @click.prevent="playSportGame(SportType.PSports, hotmatch)"
        >
          <img
            class="logo-sports"
            :src="`/images/hotmatch/${
              isCheckEmptyPSport(hotmatch) || hotmatch?.isDisable
                ? 'provider-p-sport-disable.webp'
                : 'provider-p-sport.webp'
            }`"
            alt="Logo P-Sports"
          />
          <span class="v-btn__content">Cược P-Sports</span>
        </a>
      </div>
    </div>
  </div>
</template>
<style lang="scss" scoped>
@import '~/assets/styles/base/hotmatch.scss';
</style>
